// src/components/forms/ManageTokensList.js
import React, { useState, useEffect } from 'react';
import { LoaderCircle, Copy, Ban, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom'; // 2. Import useParams and useNavigate
import { toast } from "sonner"

// Import your reusable UI components
import Button from '../ui/Button';

// Import your authenticated API hook
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';

import API_CONFIG, { FRONTEND_CONFIG } from '../../config/api'
const BASE_URL = API_CONFIG.BASE_URL

function ManageTokensList({ surveyId }) {
	const [tokens, setTokens] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const navigate = useNavigate(); // Initialize useNavigate hook

	// Get the authenticated fetch method from your hook
	const { authenticatedFetch } = useAuthenticatedApi();

	// Fetch tokens on component mount
	useEffect(() => {
		fetchTokens();
	}, [surveyId]);

	const fetchTokens = async () => {
		setIsLoading(true);
		setError(null);

		try {
			const url = BASE_URL + `/active_surveys/get-participant-token-list/${surveyId}`
			const result = await authenticatedFetch(url, {}, 'GET');
			
			if (result.status === 404) {
				navigate('/404', { state: { message: `survey with survey_id: ${surveyId} is not found`} });
				return;
			}

			if (result.status === 401) {
				setError("You are not authorized to access this survey link generation, please check that you have the proper access permission and that it is an active survey");
				setTokens(null); // Clear survey data on unauthorized
				return;
			}

			if (!result.ok) {
				let errorMessage = `Failed to fetch survey: Server responded with status ${result.status}.`;
				try {
					const errorBody = await result.json();
					if (errorBody && errorBody.detail) {
						errorMessage = errorBody.detail;
					}
				} catch (jsonError) {
				}
				console.error("ManageTokenList API Error:", errorMessage, result);
				setError(errorMessage);
				return; // Stop further execution
			}

			const datas = await result.json();
			if (datas) {
				setTokens(datas.participant_token || []);
			} else {
				console.error("API returned empty or invalid data structure:", datas);
				setError("Error API returned empty or invalid data structure. Please try later");
			}
		} catch (err) {
			setError(err.detail || 'Failed to fetch tokens. Please try again.');
		} finally {
			setIsLoading(false);
		}
	};

	const copyToClipboard = (token) => {
		navigator.clipboard.writeText(token);
		toast("✔️​ copied", {duration: 1500})
	};

	const revokeToken = async (tokenId) => {
		if (!window.confirm('You are about to revoke this participant access, this cannot be undone')) {
			return;
		}

		try {
			const response = await authenticatedFetch(
				BASE_URL + `/active_surveys/revoke-participant-token/${surveyId}/${tokenId}`,
				{},
				'PATCH'
			);
			if (!response.ok) {
				// Handle other non-2xx status codes (e.g., 500, 401, 403)
				let errorMessage = `Failed to revoke the token ${response.status}.`;
				try {
					const errorBody = await response.json();
					if (errorBody && errorBody.detail) {
						errorMessage = errorBody.detail;
					}
				} catch (jsonError) {
					// Ignore if response body isn't JSON
				}
				console.error("GenerateTokenForm API Error:", errorMessage, response);
				setError(errorMessage);
				return; // Stop further execution
			}
			const datas = await response.json();
			if (datas) {
				const message = '✅​ ' + datas 
				toast(message)
			}

			// Refresh the tokens list
			fetchTokens();
		} catch (err) {
			setError(err.detail || 'Failed to delete token. Please try again.');
		}
	};

	const formatDate = (dateString) => {
		return new Date(dateString).toLocaleString();
	};

	const isTokenExpired = (validUntil) => {
		return new Date(validUntil) < new Date();
	};

	const getTokenStatus = (token) => {
		if (isTokenExpired(token.valid_until)) {
			return { text: 'Expired', className: 'bg-red-100 text-red-800' };
		}
		if (token.use_countdown === 0) {
			return { text: 'Used Up', className: 'bg-gray-100 text-gray-800' };
		}
		if (token.is_active === false) {
			return { text: 'Revoked', className: 'bg-red-100 text-red-800' };
		}
		return { text: 'Active', className: 'bg-green-100 text-green-800' };
	};
	
	const exportCsv = (tokensList) => {
		if (!tokensList || tokensList.length === 0) {
			toast("No tokens to export");
			return;
		}
	
		try {
			// Define CSV headers
			const headers = [
				'Link',
				'Name',
				'Description',
				'Valid From',
				'Valid Until',
				'Max Use',
				'Use Countdown',
				'Is Active'
			];
	
			// Helper function to format date
			const formatDate = (dateString) => {
				if (!dateString) return '';
				try {
					const date = new Date(dateString);
					return date.toLocaleString('en-US', {
						year: 'numeric',
						month: '2-digit',
						day: '2-digit',
						hour: '2-digit',
						minute: '2-digit',
						second: '2-digit'
					});
				} catch (error) {
					return dateString;
				}
			};
	
			// Helper function to escape CSV values
			const escapeCsvValue = (value) => {
				if (value === null || value === undefined) return '';
				const stringValue = String(value);
				// If the value contains comma, newline, or double quote, wrap in quotes
				if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
					// Escape double quotes by doubling them
					return `"${stringValue.replace(/"/g, '""')}"`;
				}
				return stringValue;
			};
	
			const url = `${FRONTEND_CONFIG.BASE_URL}/public/evaa-survey/${surveyId}/`
			// Convert tokens to CSV rows
			const csvRows = tokensList.map(token => [
				escapeCsvValue(url + token.token),
				escapeCsvValue(token.name),
				escapeCsvValue(token.description),
				escapeCsvValue(formatDate(token.valid_from)),
				escapeCsvValue(formatDate(token.valid_until)),
				escapeCsvValue(token.max_use),
				escapeCsvValue(token.use_countdown),
				escapeCsvValue(token.is_active ? 'Yes' : 'No')
			]);
	
			// Combine headers and rows
			const allRows = [headers, ...csvRows];
	
			// Convert to CSV string
			const csvContent = allRows.map(row => row.join(',')).join('\n');
	
			// Create blob and download
			const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
			const link = document.createElement('a');
			
			if (link.download !== undefined) {
				const url = URL.createObjectURL(blob);
				link.setAttribute('href', url);
				
				// Generate filename with current date
				const now = new Date();
				const timestamp = now.toISOString().slice(0, 19).replace(/:/g, '-');
				const filename = `participant_tokens_${timestamp}.csv`;
				
				link.setAttribute('download', filename);
				link.style.visibility = 'hidden';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				
				// Clean up the URL object
				URL.revokeObjectURL(url);
				
				toast(`Successfully exported ${tokensList.length} tokens to CSV`);
			} else {
				throw new Error('Download not supported');
			}
		} catch (error) {
			console.error('Error exporting CSV:', error);
			toast('Failed to export tokens to CSV');
		}
	};

	if (isLoading) {
		return (
			<div className="flex justify-center items-center py-12">
				<LoaderCircle className="h-8 w-8 animate-spin text-blue-500" />
				<span className="ml-2 text-gray-600">Loading tokens...</span>
			</div>
		);
	}

	if (error) {
		return (
			<div className="text-center py-12">
				<p className="text-red-500 mb-4">{error}</p>
				<Button onClick={fetchTokens} variant="outline">
					Retry
				</Button>
			</div>
		);
	}

	return (
		<div>
			<div className="mb-6 ">
				<h2 className="text-xl font-semibold text-primary mb-2 flex items-center justify-between">
					Manage Access Tokens
					<button onClick={() => exportCsv(tokens)}
							title="export tokens to csv">
						<Download></Download> 
					</button>
				</h2>
				<p className="text-sm text-gray-600">
					View and manage all access tokens for this survey.
				</p>
			</div>

			{tokens.length === 0 ? (
				<div className="text-center py-12">
					<p className="text-gray-500">No access tokens found.</p>
					<p className="text-sm text-gray-400 mt-2">
						Create your first token using the "Generate Access Token" tab.
					</p>
				</div>
			) : (
				<div className="space-y-4">
					{tokens.map((token) => {
						const status = getTokenStatus(token);
						const survey_url = `${FRONTEND_CONFIG.BASE_URL}/public/evaa-survey/${surveyId}/${token.token}`
						return (
							<div key={token.token} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
								<div className="flex justify-between items-start mb-3">
									<div className="flex-1">
										<div className="flex items-center gap-3 mb-2">
											<h3 className="font-semibold text-gray-900">{token.name}</h3>
											<span className={`px-2 py-1 rounded-full text-xs font-medium ${status.className}`}>
												{status.text}
											</span>
										</div>
										<p className="text-sm text-gray-600 mb-2">{token.description}</p>
										
										{/* Token Value */}
										<div className="flex items-center gap-2 mb-2">
											<span className="text-sm font-medium text-gray-700">URL:</span>
											<div className="flex items-center gap-1">
												<code className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">
													{survey_url}
												</code>
												<button
													onClick={() => copyToClipboard(survey_url)}
													className="p-1 hover:bg-gray-200 rounded"
													title="Copy URL"
												>
													<Copy className="h-4 w-4" />
												</button>
											</div>
										</div>
									</div>
									
									<button
										onClick={() => revokeToken(token.token)}
										className="p-2 hover:bg-red-50 rounded-lg transition-colors group"
										title="Revoke token"
									>
										<Ban className="h-4 w-4 text-gray-400 group-hover:text-red-500" />
									</button>
								</div>
								
								{/* Token Details */}
								<div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
									<div>
										<span className="font-medium">Valid From:</span>
										<br />
										{formatDate(token.valid_from)}
									</div>
									<div>
										<span className="font-medium">Valid Until:</span>
										<br />
										{formatDate(token.valid_until)}
									</div>
									<div>
										<span className="font-medium">Usage:</span>
										<br />
										{(token.max_use - token.use_countdown) || 0} / {token.max_use}
									</div>
								</div>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
}

export default ManageTokensList;